import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import { FulfillmentAdminClient } from '../../../infrastructure/FulfillmentAdminClient';
import { ROUTES } from '../../../routes/routes';
import { describeFailure } from '../../../utils/fulfillmentErrors';
import { attemptFingerprint, AttemptKeys } from '../../../utils/attemptKeys';
import { asciiScanField } from '@/pages/fulfillment/components/asciiScanField';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';

export default function FulfillmentReturnsPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const [orderId, setOrderId] = useState('');
  const [lineNo, setLineNo] = useState('');
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const putawayKeys = useRef(new AttemptKeys());
  const inFlightRef = useRef(false);
  const [message, setMessage] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);
  const normalizedOrderId = Number(orderId);
  const normalizedLineNo = Number(lineNo);
  const locationCode = location.trim();

  const valid =
    Number.isInteger(normalizedOrderId) &&
    normalizedOrderId > 0 &&
    Number.isInteger(normalizedLineNo) &&
    normalizedLineNo > 0 &&
    locationCode !== '';

  const submit = async () => {
    if (!valid || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setBusy(true);
    setMessage(null);
    try {
      // A retry of the same put-away must carry the same key: another key on a returned line is refused.
      const response = await putawayKeys.current.run(
        attemptFingerprint([normalizedOrderId, normalizedLineNo, locationCode]),
        (idempotencyKey) =>
          client.returnPutaway(normalizedOrderId, {
            idempotencyKey,
            lineNo: normalizedLineNo,
            locationCode,
          }),
      );
      setMessage(
        response.putAway
          ? { variant: 'success', text: `Return put away — order #${orderId}, line ${lineNo}` }
          : { variant: 'danger', text: 'The return put-away was not applied. Check the line status.' },
      );
    } catch (error) {
      setMessage({
        variant: 'danger',
        text: describeFailure(error, 'The return put-away failed. Check the line status.'),
      });
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">Returns</h2>
          <div className="text-muted small">Put outbounded lines back into a return location.</div>
        </div>
        <Link to={ROUTES.fulfillment.orders} className="btn btn-outline-secondary btn-sm">
          Back to orders
        </Link>
      </div>
      {message && <Alert variant={message.variant}>{message.text}</Alert>}
      <Card>
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group controlId="return-order">
                <Form.Label>Order ID</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={orderId}
                  onChange={(event) => setOrderId(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group controlId="return-line">
                <Form.Label>Line no.</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={lineNo}
                  onChange={(event) => setLineNo(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="return-location">
                <Form.Label>Location</Form.Label>
                <CameraScanField label="Scan location with camera">
                  <Form.Control value={location} {...asciiScanField(setLocation)} />
                </CameraScanField>
              </Form.Group>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button disabled={!valid || busy} onClick={() => void submit()}>
                Put away
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}
